import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, Dialog, useMediaQuery } from "@mui/material";
import { Product } from "../../types/Product";
import {
    ProductVariant,
    ProductVariantAttributeWithValues,
    ProductVariantDetail,
} from "../../types/ProductVariant";
import { AppConfigFields } from "../../types/appConfigEntities";
import { HttpProduct } from "../../http";
import { formatMoney } from "../../styles/liquidGlass";
import { Icon, ImgPh } from "../liquid/LiquidPrimitives";
import ProductVariantSelector from "../ProductDetail/ProductVariantSelector";
import QuantitySelector from "../ProductDetail/QuantitySelector";
import {
    buildVariantAttributeDisplay,
    pickVariantForSelectedAttributes,
    resolveInitialVariantSelection,
} from "../../utils/productVariantSelection";
import { useTiendaInfo } from "../../contexts/TiendaInfoContext";
import { buildProductWhatsAppUrl } from "../../utils/whatsapp";
import { trackMetric } from "@ecommerce/analytics";

interface QuickAddDialogProps {
    open: boolean;
    product: Product;
    onClose: () => void;
}

const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] || 1;

const getSafeQuantity = (stock: number | null | undefined) => {
    if (stock == null) return minimumPurchase;
    if (stock <= 0) return minimumPurchase;
    return Math.min(Math.max(minimumPurchase, 1), stock);
};

export default function QuickAddDialog({ open, product, onClose }: QuickAddDialogProps) {
    const isMobile = useMediaQuery("(max-width:768px)");
    const tiendaInfo = useTiendaInfo();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [variantAttributes, setVariantAttributes] = useState<ProductVariantAttributeWithValues[]>(
        [],
    );
    const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
    const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
    const [selectedVariantAttributes, setSelectedVariantAttributes] = useState<
        { name: string; value: string }[]
    >([]);
    const [quantity, setQuantity] = useState(minimumPurchase);

    useEffect(() => {
        if (!open) return;

        let cancelled = false;

        const loadQuickAddData = async () => {
            try {
                setLoading(true);
                setError(null);
                setVariantAttributes([]);
                setSelectedAttributes({});
                setSelectedVariant(null);
                setSelectedVariantAttributes([]);

                const [attributes, defaultVariantDetail, productVariantDetails] = await Promise.all(
                    [
                        HttpProduct.getProductVariantAttributesWithValues<
                            ProductVariantAttributeWithValues[]
                        >(product.id),
                        product.idProductoVarianteDefault
                            ? HttpProduct.getProductVariantDetail<ProductVariantDetail>(
                                  product.idProductoVarianteDefault,
                              )
                            : Promise.resolve(null),
                        HttpProduct.getProductVariantsByProductId<ProductVariantDetail>(
                            product.id,
                        ).catch(() => []),
                    ],
                );

                if (cancelled) return;

                const normalizedAttributes = attributes || [];
                setVariantAttributes(normalizedAttributes);

                if (defaultVariantDetail?.productoVariante) {
                    const variant = defaultVariantDetail.productoVariante as ProductVariant;
                    const {
                        selectedAttributes: initialSelected,
                        selectedVariantAttributes: attrDisplay,
                    } = resolveInitialVariantSelection(
                        normalizedAttributes,
                        defaultVariantDetail,
                        (productVariantDetails || []).map((detail) => detail.productoVariante),
                    );

                    // Derivar labels desde la selección (más confiable que el SP)
                    setSelectedVariant(variant);
                    setSelectedVariantAttributes(attrDisplay);
                    setSelectedAttributes(initialSelected);
                    const variantPermite =
                        variant.permiteOrdenSinStock ?? product.permiteOrdenSinStock;
                    setQuantity(
                        getSafeQuantity(variantPermite ? null : (variant.stock ?? product.stock)),
                    );
                } else {
                    setQuantity(
                        getSafeQuantity(product.permiteOrdenSinStock ? null : product.stock),
                    );
                }
            } catch (err) {
                console.error("Error loading quick add data:", err);
                if (!cancelled) setError("No pudimos cargar las variantes de este producto.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadQuickAddData();

        return () => {
            cancelled = true;
        };
    }, [open, product.id, product.idProductoVarianteDefault, product.stock]);

    useEffect(() => {
        if (!open || loading || variantAttributes.length === 0) return;

        const selectedAttributeIds = Object.keys(selectedAttributes);
        if (selectedAttributeIds.length !== variantAttributes.length) return;

        let cancelled = false;

        const loadSelectedVariant = async () => {
            try {
                const variants = await HttpProduct.getProductVariantByAttributesValues(product.id, {
                    idsProductoAtributos: selectedAttributeIds,
                    idsProductoAtributoValores: selectedAttributeIds.map(
                        (attrId) => selectedAttributes[attrId],
                    ),
                });

                const matchingVariant = pickVariantForSelectedAttributes(
                    variants || [],
                    variantAttributes,
                    selectedAttributes,
                    product.idProductoVarianteDefault,
                );

                if (cancelled || !matchingVariant) return;

                const detail = await HttpProduct.getProductVariantDetail(matchingVariant.id);
                if (cancelled) return;

                const variant = detail.productoVariante as ProductVariant;
                setSelectedVariant(variant);

                // Derivar labels desde la selección del usuario (evita inconsistencias del SP)
                const attrDisplay = buildVariantAttributeDisplay(
                    variantAttributes,
                    selectedAttributes,
                );
                setSelectedVariantAttributes(attrDisplay);
                const variantPermite2 =
                    variant.permiteOrdenSinStock ?? product.permiteOrdenSinStock;
                setQuantity(
                    getSafeQuantity(variantPermite2 ? null : (variant.stock ?? product.stock)),
                );
            } catch (err) {
                console.error("Error loading selected variant:", err);
            }
        };

        loadSelectedVariant();

        return () => {
            cancelled = true;
        };
    }, [loading, open, product.id, product.stock, selectedAttributes, variantAttributes.length]);

    const handleAttributeChange = (attributeId: string, valueId: string) => {
        setSelectedAttributes((prev) => ({
            ...prev,
            [attributeId]: valueId,
        }));
    };

    const rawStock = selectedVariant?.stock ?? product.stock;
    const permiteOrdenSinStock =
        selectedVariant?.permiteOrdenSinStock ?? product.permiteOrdenSinStock;
    const stock = permiteOrdenSinStock ? null : rawStock;
    const price = selectedVariant?.precio ?? product.precio ?? 0;
    const showPrice = product.mostrarPrecio !== false;
    const isConsultOnly = price === 0;
    const imageForCart = selectedVariant?.urlImagenPrincipal || product.urlImagenPrincipal;
    const variantSummary = useMemo(
        () => selectedVariantAttributes.map((attr) => `${attr.name}: ${attr.value}`).join(" · "),
        [selectedVariantAttributes],
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? "24px" : "32px",
                    overflow: "hidden",
                    background: "rgba(255,250,240,0.86)",
                    border: "1px solid rgba(255,255,255,0.7)",
                    boxShadow: "0 30px 80px -24px rgba(60,40,20,0.35)",
                    backdropFilter: "blur(36px) saturate(180%)",
                    mx: isMobile ? 1.5 : undefined,
                },
            }}
            BackdropProps={{
                sx: {
                    background: "rgba(20,16,12,0.18)",
                    backdropFilter: "blur(2px)",
                },
            }}
        >
            <div style={{ padding: isMobile ? 16 : 24 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 18,
                    }}
                >
                    <div>
                        <div className="eyebrow" style={{ marginBottom: 6 }}>
                            Agregar al carrito
                        </div>
                        <h2
                            style={{
                                margin: 0,
                                fontSize: 26,
                                fontWeight: 500,
                                letterSpacing: "-0.03em",
                                color: "var(--ink-900)",
                            }}
                        >
                            {product.nombre}
                        </h2>
                    </div>
                    <button
                        type="button"
                        className="pill"
                        style={{ padding: 12 }}
                        onClick={onClose}
                        aria-label="Cerrar"
                    >
                        <Icon.close />
                    </button>
                </div>

                {loading ? (
                    <div
                        style={{
                            minHeight: 280,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 12,
                        }}
                    >
                        <CircularProgress size={22} sx={{ color: "var(--ink-900)" }} />
                        <span style={{ color: "var(--ink-500)", fontSize: 13 }}>
                            Cargando variantes...
                        </span>
                    </div>
                ) : error ? (
                    <div
                        className="lg-strong"
                        style={{ padding: 24, borderRadius: 22, color: "var(--ink-700)" }}
                    >
                        {error}
                    </div>
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : "0.85fr 1.15fr",
                            gap: 20,
                            alignItems: "start",
                        }}
                    >
                        {!isMobile && (
                            <div
                                style={{
                                    position: "relative",
                                    borderRadius: 26,
                                    overflow: "hidden",
                                    minHeight: 390,
                                }}
                            >
                                <ImgPh
                                    label={product.nombre}
                                    src={imageForCart}
                                    ratio="auto"
                                    style={{
                                        height: 390,
                                        borderRadius: 26,
                                        background:
                                            "linear-gradient(160deg, var(--cream-100), var(--cream-300))",
                                    }}
                                />
                                <div
                                    className="lg"
                                    style={{
                                        position: "absolute",
                                        left: 14,
                                        right: 14,
                                        bottom: 14,
                                        padding: "14px 16px",
                                        borderRadius: 18,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        alignItems: "center",
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        {showPrice && (
                                            <div
                                                className="mono"
                                                style={{
                                                    fontSize: isConsultOnly ? 15 : 22,
                                                    fontWeight: 600,
                                                    color: isConsultOnly
                                                        ? "var(--ink-500)"
                                                        : undefined,
                                                }}
                                            >
                                                {isConsultOnly
                                                    ? "Consultar precio"
                                                    : formatMoney(price)}
                                            </div>
                                        )}
                                        {variantSummary && (
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    color: "var(--ink-500)",
                                                    fontSize: 12,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {variantSummary}
                                            </div>
                                        )}
                                    </div>
                                    <span
                                        className="mono"
                                        style={{ color: "var(--ink-500)", fontSize: 11 }}
                                    >
                                        {stock == null ? "Disponible" : `${stock} EN STOCK`}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div style={{ display: "grid", gap: 14 }}>
                            {isMobile && (
                                <div
                                    className="lg"
                                    style={{
                                        padding: "14px 16px",
                                        borderRadius: 18,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 12,
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        {showPrice && (
                                            <div
                                                className="mono"
                                                style={{
                                                    fontSize: isConsultOnly ? 15 : 22,
                                                    fontWeight: 600,
                                                    color: isConsultOnly
                                                        ? "var(--ink-500)"
                                                        : undefined,
                                                }}
                                            >
                                                {isConsultOnly
                                                    ? "Consultar precio"
                                                    : formatMoney(price)}
                                            </div>
                                        )}
                                        {variantSummary && (
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    color: "var(--ink-500)",
                                                    fontSize: 12,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {variantSummary}
                                            </div>
                                        )}
                                    </div>
                                    <span
                                        className="mono"
                                        style={{
                                            color: "var(--ink-500)",
                                            fontSize: 11,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {stock == null ? "Disponible" : `${stock} EN STOCK`}
                                    </span>
                                </div>
                            )}

                            <ProductVariantSelector
                                variantAttributes={variantAttributes}
                                selectedAttributes={selectedAttributes}
                                selectedVariant={selectedVariant}
                                onChange={handleAttributeChange}
                            />

                            {variantAttributes.length === 0 && (
                                <div
                                    className="lg-strong"
                                    style={{ padding: "18px 22px", borderRadius: 22 }}
                                >
                                    <div className="eyebrow" style={{ marginBottom: 8 }}>
                                        Variante
                                    </div>
                                    <div style={{ color: "var(--ink-700)", fontSize: 13 }}>
                                        Se agregara la variante disponible de este producto.
                                    </div>
                                </div>
                            )}

                            {isConsultOnly ? (
                                <a
                                    href={buildProductWhatsAppUrl(
                                        product,
                                        tiendaInfo?.whatsappNumero,
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() =>
                                        trackMetric("PRODUCT_WHATSAPP", {
                                            entityId: product.id.toString(),
                                        })
                                    }
                                    className="btn-primary"
                                    style={{ width: "100%", textDecoration: "none" }}
                                >
                                    <Icon.whatsapp /> Consultar por WhatsApp
                                </a>
                            ) : (
                                <QuantitySelector
                                    productId={product.id.toString()}
                                    variantId={selectedVariant?.id}
                                    productName={product.nombre}
                                    productPrice={price}
                                    productImage={imageForCart}
                                    quantity={quantity}
                                    stock={stock}
                                    onQuantityChange={setQuantity}
                                    minQuantity={minimumPurchase}
                                    onAfterAddToCart={onClose}
                                    attributes={selectedVariantAttributes}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
}
